import { useEffect, useRef, useState } from 'react';

const MIN_HEIGHT = 420;
const MAX_HEIGHT = 1180;

export function InspectFeature({ paused = false }: { paused?: boolean }) {
  const section = useRef<HTMLElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(520);
  const [ready, setReady] = useState(false);
  const source = useRef<string>();
  if (ready && !source.current)
    source.current =
      '/landing/inspect-feature/index.html' + (paused ? '?paused=1' : '');
  const syncMotion = () =>
    frame.current?.contentWindow?.postMessage(
      { type: 'directordeck:inspect-motion', paused },
      window.location.origin,
    );
  useEffect(() => {
    frame.current?.contentWindow?.postMessage(
      { type: 'directordeck:inspect-motion', paused },
      window.location.origin,
    );
  }, [paused, ready]);

  useEffect(() => {
    const element = section.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setReady(true);
        observer.disconnect();
      },
      { rootMargin: '250px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const receiveHeight = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== frame.current?.contentWindow
      )
        return;
      if (event.data?.type !== 'directordeck:inspect-height') return;
      const nextHeight = Number(event.data.height);
      if (!Number.isFinite(nextHeight)) return;
      setHeight(
        Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.ceil(nextHeight))),
      );
    };
    window.addEventListener('message', receiveHeight);
    return () => window.removeEventListener('message', receiveHeight);
  }, []);

  return (
    <section
      ref={section}
      className="inspect-feature-section section-rule"
      id="features"
      aria-label="How DirectorDeck creates a presentation"
    >
      {ready ? (
        <iframe
          ref={frame}
          className="inspect-feature-frame"
          src={source.current}
          onLoad={syncMotion}
          title="Create a presentation in five steps: Describe, Refine, Transform, Review, and Present"
          loading="lazy"
          style={{ height }}
          sandbox="allow-scripts allow-same-origin"
        />
      ) : (
        <div
          className="inspect-feature-placeholder"
          style={{ height }}
          aria-hidden="true"
        />
      )}
    </section>
  );
}
